import { formatDate, ViewportScroller } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';

import { Subscription, throwError } from 'rxjs';
import { catchError, finalize, mergeMap } from 'rxjs/operators';
import { addDays } from '../../core/helpers';

import { DynamicScriptLoaderService, FileExportService } from '../../core/services';
import { OrderService } from '../../core/services/order.service';
import { ToastService } from '../../core/services/toast.service';

@Component({
    selector: 'gs-orders',
    templateUrl: './orders.component.html',
    standalone: false
})
export class OrdersComponent implements OnInit, OnDestroy {
  orders: any[];
  filteredOrders: any[] = [];
  filter: string = "all";
  pdfScriptsLoaded: boolean = false;

  private subscriptions: Subscription[] = [];

  constructor(
    private orderService: OrderService,
    private toastService: ToastService,
    private fileExportService: FileExportService,
    private dynamicScriptLoaderService: DynamicScriptLoaderService,
    private viewportScroller: ViewportScroller
  ) {

    this.subscriptions.push(
      this.dynamicScriptLoaderService
        .loadMultipleScriptsByName('jspdf', 'xlsx')
        .pipe(mergeMap(() => this.dynamicScriptLoaderService
          .loadScriptByName('jspdf.autotable')))
        .subscribe(() => {
          this.pdfScriptsLoaded = true;
        })
    );
  }

  ngOnInit() {
    this.getOrders();
  }

  ngOnDestroy() {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  getOrders() {
    this.subscriptions.push(
      this.orderService.getOrders()
        .pipe(
          catchError(e => {
            this.toastService.checkErrorForStatus(e);
            return throwError(e);
          }),
          finalize(() => { })
        )
        .subscribe((response: any) => {
          this.orders = response;
          this.filterOrders();
        })
    );
  }

  exportAsPdf() {
    if (!this.filteredOrders) {
      return;
    }

    const exportData = this.filteredOrders.map(e => {
      const item = Object.assign({}, e);
      item.submittedDate = formatDate(item.submittedDate, 'HH:mm dd/MM/yyyy', 'en');
      return item;
    });

    this.fileExportService.exportAsPdf(
      exportData,
      ['submittedDate', 'partnerName', 'amount', 'orderNo'],
      ['Data', 'Partener', 'Suma', 'Numar comanda'],
      `comenzi-giftshare-${formatDate(new Date(), 'yyyy-MM-dd-HH-mm', 'en')}`
    );
  }

  exportAsExcel() {
    if (!this.filteredOrders) {
      return;
    }

    const exportData = this.filteredOrders.map(e => {
      const item = Object.assign({}, e);
      item.submittedDate = formatDate(item.submittedDate, 'HH:mm dd/MM/yyyy', 'en');
      return item;
    });

    this.fileExportService.exportAsExcel(
      exportData,
      ['submittedDate', 'partnerName', 'amount', 'orderNo'],
      ['Data', 'Partener', 'Suma', 'Numar comanda'],
      `comenzi-giftshare-${formatDate(new Date(), 'yyyy-MM-dd-HH-mm', 'en')}`
    );
  }

  onFilterChange() {
    this.filterOrders();
    this.scroll();
  }

  private filterOrders() {
    if (!this.orders) {
      return;
    }

    switch (this.filter) {
      case 'last-week': {
        const referenceDate = addDays(new Date(), -7);
        this.filteredOrders = this.orders.filter((x) => x.submittedDate >= referenceDate );
        break;
      }
      case 'last-month': {
        const referenceDate = addDays(new Date(), -30);
        this.filteredOrders = this.orders.filter((x) => x.submittedDate >= referenceDate );
        break;
      }
      case 'pending': {
        this.filteredOrders = this.orders.filter(x => x.status.toLowerCase() == 'pending');
        break;
      }
      case 'processing': {
        this.filteredOrders = this.orders.filter(x => x.status.toLowerCase() == 'processing');
        break;
      }
      case 'failed': {
        this.filteredOrders = this.orders.filter(x => x.status.toLowerCase() == 'failed');
        break;
      }
      case 'completed': {
        this.filteredOrders = this.orders.filter(x => x.status.toLowerCase() == 'completed');
        break;
      }
      default: {
        this.filteredOrders = [...this.orders];
        break;
      }
    }
  }

  private scroll() {
    this.viewportScroller.scrollToPosition([0, 0]);
  }
}
